import { combineReducers } from 'redux';
import { getType } from 'typesafe-actions';
import { actions } from '..';
import { Email } from '../../model';
import { Actions, StoreState } from '../types';

type SendEmail = { to: string, cc: string, out: boolean } & Email

export enum Sending {
  Writing = 0,
  Sending = 1,
  Sent = 3,
}

export interface EmailState {
  original: Email
  reply?: SendEmail
  sending: Sending
  accountId?: string
}

type Emails = Record<string, EmailState | undefined>

export function emails(state: Emails = {}, action: Actions): Emails {
  switch (action.type) {
    case getType(actions.setCreateNewOutGoing): {
      const { mediaId, original, reply } = action.payload
      return {
        ...state,
        [mediaId]: {
          original: original,
          reply: { ...reply, to: '', cc: '', out: true },
          sending: Sending.Writing,
        },
      }
    }

    case getType(actions.fetchEmailReadResponse): {
      const { id, email, accountId } = action.payload

      return {
        ...state,
        [id]: {
          original: {
            ...email, attachments: email.attachments.sort((a, b) => {
              if (a.isInLine && !b.isInLine) return 1
              if (!a.isInLine && b.isInLine) return -1

              if (a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase()) return 1
              if (a.name.toLocaleLowerCase() < b.name.toLocaleLowerCase()) return -1

              return 0
            }),
          },
          sending: Sending.Writing,
          accountId: accountId,
        },
      }
    }

    case getType(actions.emailSent): {
      const emails = state[action.payload]
      if (!emails) { return state }

      if (emails.reply && emails.reply.out) {
        return {
          ...state,
          [action.payload]: {
            ...emails,
            sending: Sending.Sent,
          },
        }
      }

      return {
        ...state,
        [action.payload]: {
          ...emails,
          sending: Sending.Sent,
          reply: undefined,
        },
      }
    }


    case getType(actions.cancelReply): {
      const emails = state[action.payload]
      if (!emails) { return state }

      if (emails.reply && emails.reply.out) {
        return {
          ...state,
          [action.payload]: {
            ...emails,
            sending: Sending.Writing,
          },
        }
      }

      return {
        ...state,
        [action.payload]: {
          ...emails,
          sending: Sending.Writing,
          reply: undefined,
        },
      }
    }

    case getType(actions.sendEmail): {
      const emails = state[action.payload]
      if (!emails) { return state }
      return {
        ...state,
        [action.payload]: {
          ...emails,
          sending: Sending.Sending,
        },
      }
    }

    case getType(actions.sendEmailFailed): {
      const emails = state[action.payload]
      if (!emails) { return state }
      return {
        ...state,
        [action.payload]: {
          ...emails,
          sending: Sending.Writing,
        },
      }
    }

    case getType(actions.setReplyEmail): {
      const { mediaId, email, outbound } = action.payload
      const emails = state[mediaId]
      if (!emails) { return state }

      return {
        ...state,
        [mediaId]: {
          ...emails,
          reply: {
            ...email,
            to: email.toRecipients.map(r => r.address).join('; '),
            cc: email.ccRecipients.map(r => r.address).join('; '),
            out: outbound,
          },
          sending: Sending.Writing,
        },
      }
    }

    case getType(actions.removeMediaItem): {
      const emails = state[action.payload]
      if (!emails) { return state }

      const { [action.payload]: _, ...rest } = state
      return rest
    }

    case getType(actions.setEmailTo): {
      const { mediaId, to } = action.payload
      return editEmail(state, mediaId, 'to', to)
    }

    case getType(actions.setEmailCc): {
      const { mediaId, cc } = action.payload
      return editEmail(state, mediaId, 'cc', cc)
    }

    case getType(actions.setEmailSubject): {
      const { mediaId, subject } = action.payload
      return editEmail(state, mediaId, 'subject', subject)
    }

    case getType(actions.setEmailBody): {
      const { mediaId, body } = action.payload
      return editEmail(state, mediaId, 'body', body)
    }

    case getType(actions.setEmailFileAttachment): {
      const { mediaId, emailId, attachment } = action.payload
      const emails = state[mediaId]
      if (!emails) { return state }

      if (!emails.reply || emails.reply.id !== emailId) { return state }

      return {
        ...state,
        [mediaId]: {
          ...emails,
          reply: {
            ...emails.reply,
            attachments: [...emails.reply.attachments, attachment],
          },
        },
      }
    }

    case getType(actions.removeEmailFileAttachment): {
      const { mediaId, emailId, attachmentId } = action.payload
      const emails = state[mediaId]
      if (!emails) { return state }

      if (!emails.reply || emails.reply.id !== emailId) { return state }

      const index = emails.reply.attachments.findIndex(x => x.id === attachmentId)
      const length = emails.reply.attachments.length

      if (index === -1) { return state }

      return {
        ...state,
        [mediaId]: {
          ...emails,
          reply: {
            ...emails.reply,
            attachments: (index === 0 && length === 1) ? [] : [...emails.reply.attachments.slice(0, index), ...emails.reply.attachments.slice(index + 1)],
          },
        },
      }
    }
  }

  return state
}

function editEmail<K extends keyof SendEmail>(state: Emails, mediaId: string, key: K, value: SendEmail[K]): Emails {
  const emails = state[mediaId]
  if (!emails || !emails.reply) { return state }

  return {
    ...state,
    [mediaId]: {
      ...emails,
      reply: {
        ...emails.reply,
        [key]: value,
      },
    },
  }
}


export default combineReducers({
  emails,

})

function getRoot(state: StoreState) { return state.emails }
function getEmails(state: StoreState) { return getRoot(state).emails }

export function selectEmail(state: StoreState, mediaId: string) {
  return getEmails(state)[mediaId] || null
}

export function selectReplyEmail(state: StoreState, mediaId: string) {
  const emails = getEmails(state)[mediaId]
  return emails && emails.reply || null
}

export function selectEmailSending(state: StoreState, mediaId: string) {
  const emails = getEmails(state)[mediaId]
  return emails && emails.sending
}
